import { defineComponent as _defineComponent } from 'vue'
import { computed, ref } from 'vue';
import PaymentScheduleForm from './PaymentScheduleForm.vue';
import PaymentScheduleReview from './PaymentScheduleReview.vue';
import { CreateTradeRequest, PremiumPaymentType, VanillaPriceResponse } from 'ah-api-gateways';
import { PaymentFormDetails } from '@/app/models/payments';
import { useRequestManager } from 'ah-common-lib/src/requestManager/useRequestManager';
import { getServices } from '@/app/services';
import { useOnBehalfOf } from 'ah-common-lib/src/onBehalfOf/useInjectedOBO';
import { useToast } from 'ah-common-lib/src/toast';
import { mergeMap } from 'rxjs/operators';
import { waitForEntityCreation } from 'ah-requests';
import { formatDate } from 'ah-common-lib/src/helpers/time';

enum PaymentScheduleCreatorSteps {
  PAYMENT_SCHEDULE_CREATOR = 'paymentScheduleCreator',
  PAYMENT_SCHEDULE_REVIEW = 'paymentScheduleReview',
}


export default /*#__PURE__*/_defineComponent({
  __name: 'PaymentScheduleCreator',
  props: {
  flowTitle: {
    type: String,
    default: 'Create Payment Schedule',
  },
},
  setup(__props) {

const props = __props;



const paymentScheduleForm = ref<InstanceType<typeof PaymentScheduleForm>>();

const requestManager = useRequestManager().manager;

const services = getServices();

const toast = useToast();

const onBehalfOfClient = useOnBehalfOf();

const stage = ref<PaymentScheduleCreatorSteps>(PaymentScheduleCreatorSteps.PAYMENT_SCHEDULE_CREATOR);

const title = computed(() => {
  return stage.value === PaymentScheduleCreatorSteps.PAYMENT_SCHEDULE_CREATOR
    ? props.flowTitle
    : 'Review Payment Details & Schedule';
});

const solution = ref<VanillaPriceResponse>();

const model = ref<PaymentFormDetails>();

const isLoading = ref<boolean>(false);

const isInvalid = ref<boolean>(true);

const disabledDates = ref<string[]>();

const secondaryButton = computed(() => {
  return stage.value === PaymentScheduleCreatorSteps.PAYMENT_SCHEDULE_CREATOR ? 'Cancel' : 'Go Back';
});

const primaryButton = computed(() => {
  return stage.value === PaymentScheduleCreatorSteps.PAYMENT_SCHEDULE_CREATOR
    ? 'Review Payment Schedule'
    : 'Book Payment Schedule';
});

function backStep() {
  if (stage.value === PaymentScheduleCreatorSteps.PAYMENT_SCHEDULE_CREATOR) {
    paymentScheduleForm.value?.reset();
    model.value = undefined;
  } else if (stage.value === PaymentScheduleCreatorSteps.PAYMENT_SCHEDULE_REVIEW) {
    stage.value = PaymentScheduleCreatorSteps.PAYMENT_SCHEDULE_CREATOR;
  }
}

function composeCreateTradeRequest() {
  if (solution.value && model.value) {
    return {
      priceId: solution.value.id,
      hedgingProduct: solution.value.hedgingProduct,
      syncPendingFunds: false,
      premiumCurrency: model.value.sellCurrency,
      mvpCustomerEmail: model.value.mvpCustomerEmail,
      mvpInvoiceNumber: model.value.mvpInvoiceNumber,
      mvpDescription: model.value.mvpDescription,
      mvpFrequency: model.value.mvpFrequency,
      mvpNumPayments: model.value.mvpNumPayments,
      mvpFirstPaymentDate: formatDate(model.value.mvpFirstPaymentDate, 'yyyy-MM-dd'),
      premiumPaymentType: PremiumPaymentType.UPFRONT,
    } as CreateTradeRequest;
  }

  return undefined;
}

function nextStep() {
  if (stage.value === PaymentScheduleCreatorSteps.PAYMENT_SCHEDULE_CREATOR) {
    stage.value = PaymentScheduleCreatorSteps.PAYMENT_SCHEDULE_REVIEW;
  } else if (stage.value === PaymentScheduleCreatorSteps.PAYMENT_SCHEDULE_REVIEW) {
    const tradeRequest = composeCreateTradeRequest();

    if (!tradeRequest) {
      throw 'Not enough date for making a trade request';
    }

    requestManager
      .sameOrCancelAndNew(
        'createTrade',
        services.trade
          .createTrade(tradeRequest, onBehalfOfClient.value?.id, {
            errors: { silent: true },
          })
          .pipe(
            mergeMap((idEntity) =>
              waitForEntityCreation(() => services.trade.getTrade(idEntity.id, { errors: { silent: true } }))
            )
          ),
        tradeRequest
      )
      .subscribe(
        (response) => {
          if (response !== null) {
            toast.success('Payment schedule booked successfully!');
            stage.value = PaymentScheduleCreatorSteps.PAYMENT_SCHEDULE_CREATOR;
            paymentScheduleForm.value?.reset();
            model.value = undefined;
          }
        },
        () => {
          toast.error('There was an error and booking the payment schedule has failed!');
          stage.value = PaymentScheduleCreatorSteps.PAYMENT_SCHEDULE_CREATOR;
          paymentScheduleForm.value?.reset();
          model.value = undefined;
        }
      );
  }
}

function updateValidations(anyInvalid: boolean) {
  isInvalid.value = anyInvalid;
}

function updateLoading(loading: boolean) {
  isLoading.value = loading;
}

return { __sfc: true,props, PaymentScheduleCreatorSteps, paymentScheduleForm, requestManager, services, toast, onBehalfOfClient, stage, title, solution, model, isLoading, isInvalid, disabledDates, secondaryButton, primaryButton, backStep, composeCreateTradeRequest, nextStep, updateValidations, updateLoading, PaymentScheduleForm, PaymentScheduleReview }
}

})